import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Twitter, Loader2, RefreshCw, Copy } from "lucide-react";
import { useGenerateChangelogTweet } from "@/hooks/use-changelog";
import { toast } from "sonner";

const MAX_TWEET_LENGTH = 280;

interface ChangelogTweetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entryId: string | null;
}

export function ChangelogTweetDialog({
  open,
  onOpenChange,
  entryId,
}: ChangelogTweetDialogProps) {
  const [tweet, setTweet] = useState("");
  const generateMutation = useGenerateChangelogTweet();
  const hasTriggered = useRef(false);

  useEffect(() => {
    if (!open || !entryId) {
      hasTriggered.current = false;
      return;
    }

    if (hasTriggered.current) return;
    hasTriggered.current = true;

    setTweet("");
    generateMutation.reset();
    generateMutation.mutateAsync(entryId).then((result) => {
      setTweet(result.tweet);
    }).catch(() => {
      // Error handled by mutation onError
    });
  }, [open, entryId]);

  const handleRegenerate = () => {
    if (!entryId) return;

    setTweet("");
    generateMutation.mutateAsync(entryId).then((result) => {
      setTweet(result.tweet);
    }).catch(() => {
      // Error handled by mutation onError
    });
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(tweet);
      toast.success("클립보드에 복사되었습니다");
    } catch {
      toast.error("복사에 실패했습니다");
    }
  };

  const isOverLimit = tweet.length > MAX_TWEET_LENGTH;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Twitter className="h-5 w-5" />
            트윗 생성
          </DialogTitle>
          <DialogDescription>
            체인지로그를 기반으로 AI가 트윗을 생성합니다.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          {generateMutation.isPending ? (
            <div
              className="flex items-center justify-center py-12"
              role="status"
              aria-label="트윗 생성 중"
            >
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <Textarea
                aria-label="생성된 트윗"
                value={tweet}
                readOnly
                rows={5}
                className="resize-none break-all"
              />
              <p
                className={`text-xs text-right ${isOverLimit ? "text-destructive" : "text-muted-foreground"}`}
              >
                {tweet.length}/{MAX_TWEET_LENGTH}
                {isOverLimit &&
                  ` (${tweet.length - MAX_TWEET_LENGTH}자 초과)`}
              </p>
            </>
          )}
        </div>

        {!generateMutation.isPending && tweet && (
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              닫기
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleRegenerate}
              disabled={generateMutation.isPending}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              재생성
            </Button>
            <Button type="button" onClick={handleCopy}>
              <Copy className="mr-2 h-4 w-4" />
              복사
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
